import { useCallback } from 'react';
import { useStatsigClient } from '@statsig/react-bindings';
import type { ITourAnalyticsConfig, ITourStepConfig } from '../types';
import {
  buildEventMetadata,
  getTourEventName,
  resolveAnalyticsConfig,
} from '../analytics';

/**
 * Hook that provides analytics event emitters for tour lifecycle events.
 * Integrates with Statsig for event logging.
 *
 * @param tourId - The unique tour identifier
 * @param analyticsConfig - Optional analytics configuration
 * @param steps - Tour steps for step-level metadata
 * @returns Object with methods to track tour events
 */
export function useTourAnalytics(
  tourId: string,
  analyticsConfig?: ITourAnalyticsConfig,
  steps: ITourStepConfig[] = [],
) {
  const { logEvent } = useStatsigClient();
  const config = resolveAnalyticsConfig(analyticsConfig, tourId);

  const trackStarted = useCallback(() => {
    if (!config.enabled) return;

    logEvent(
      getTourEventName(config.eventPrefix, 'started'),
      tourId,
      buildEventMetadata(
        tourId,
        config.metadata,
        undefined,
        undefined,
        steps.length,
      ),
    );
  }, [
    config.enabled,
    config.eventPrefix,
    config.metadata,
    logEvent,
    tourId,
    steps.length,
  ]);

  const trackCompleted = useCallback(() => {
    if (!config.enabled) return;

    logEvent(
      getTourEventName(config.eventPrefix, 'completed'),
      tourId,
      buildEventMetadata(
        tourId,
        config.metadata,
        undefined,
        undefined,
        steps.length,
      ),
    );
  }, [
    config.enabled,
    config.eventPrefix,
    config.metadata,
    logEvent,
    tourId,
    steps.length,
  ]);

  const trackSkipped = useCallback(
    (atStepIndex: number) => {
      if (!config.enabled) return;

      const step = steps[atStepIndex];
      logEvent(
        getTourEventName(config.eventPrefix, 'skipped'),
        tourId,
        buildEventMetadata(
          tourId,
          config.metadata,
          step,
          atStepIndex,
          steps.length,
        ),
      );
    },
    [
      config.enabled,
      config.eventPrefix,
      config.metadata,
      logEvent,
      tourId,
      steps,
    ],
  );

  const trackStepViewed = useCallback(
    (stepIndex: number) => {
      if (!config.enabled) return;

      const step = steps[stepIndex];
      logEvent(
        getTourEventName(config.eventPrefix, 'step_viewed'),
        tourId,
        buildEventMetadata(
          tourId,
          config.metadata,
          step,
          stepIndex,
          steps.length,
        ),
      );
    },
    [
      config.enabled,
      config.eventPrefix,
      config.metadata,
      logEvent,
      tourId,
      steps,
    ],
  );

  return {
    trackStarted,
    trackCompleted,
    trackSkipped,
    trackStepViewed,
  };
}
